import { useRef, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { Dialog as Base } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { DialogCloseProvider, useDeferredClose } from './deferred-close';
import { escapeCloses, useEscapeAnywhere } from './escape';
import { Icon } from './icons';
import { IconButton } from './IconButton';

const PLACEMENTS = {
  'top-right': 'right-4 top-16 w-72 max-h-[calc(100dvh-5rem)]',
  right: 'right-4 top-16 bottom-4 w-80',
  /** Above the bottom toolbar, centred (paste a link). */
  bottom: 'bottom-20 left-1/2 w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2',
} as const;

/**
 * Non-modal side panel (P16-2 Ruling 7): no backdrop, the board stays usable. Its close button, Escape from anywhere
 * (except from a text surface or another layer) and a press outside close it; it takes the focus when it opens and
 * gives it to `finalFocus` (the canvas in the editor), or back to what opened it. Rendered open, like `Dialog`: `onClose`
 * runs once the exit animation is over (P18).
 */
export function Panel({
  title,
  closeLabel,
  onClose,
  children,
  initialFocus,
  finalFocus,
  placement = 'top-right',
  toggle,
  className,
}: {
  title: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children?: ReactNode;
  /** What takes the focus on open (the panel itself by default, so no tooltip pops on its close button). */
  initialFocus?: ComponentProps<typeof Base.Popup>['initialFocus'];
  finalFocus?: ComponentProps<typeof Base.Popup>['finalFocus'];
  placement?: keyof typeof PLACEMENTS;
  /** The button that opens and closes it: a press there is not "outside", its own click closes it (backlog §19). */
  toggle?: RefObject<HTMLElement | null>;
  className?: string;
}) {
  const popup = useRef<HTMLDivElement>(null);
  const { open, request, complete } = useDeferredClose(onClose);
  const close = useEscapeAnywhere(popup, () => void request());
  return (
    <DialogCloseProvider value={request}>
      <Base.Root
        open={open}
        onOpenChangeComplete={complete}
        modal={false}
        onOpenChange={(next, details) => {
          if (next) return;
          if (details.reason === 'escape-key' && !escapeCloses(details.event, popup.current)) return details.cancel();
          if (details.reason === 'outside-press' && toggle?.current?.contains(details.event.target as Node)) return details.cancel();
          close();
        }}
      >
        <Base.Portal>
          <Base.Popup
            ref={popup}
            {...{ [CANVAS_UI]: '' }}
            initialFocus={initialFocus ?? popup}
            finalFocus={finalFocus}
            className={cn(
              'os-pop fixed z-40 flex flex-col overflow-y-auto rounded-2xl bg-chrome p-3 font-sans text-sm text-fg shadow-float outline-none',
              PLACEMENTS[placement],
              className,
            )}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <Base.Title className="font-semibold">{title}</Base.Title>
              <IconButton label={closeLabel} icon={<Icon icon={X} size={16} />} size="sm" onClick={close} />
            </div>
            {children}
          </Base.Popup>
        </Base.Portal>
      </Base.Root>
    </DialogCloseProvider>
  );
}
